import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, shareReplay, tap } from 'rxjs';
import { Events } from 'src/app/shared/constants/events.constant';
import { JobRejectModel } from 'src/app/job/models/job-detail/job-reject/job-reject.model';
import { JobOverviewResult } from 'src/app/job/models/job-detail/overview/job-detail-overview-response.model';
import { AssignTradeRequest } from 'src/app/job/models/job-post/assign-trade-request.model';
import { JobPostRequest } from 'src/app/job/models/job-post/job-post-request.model';
import { RelatedJobs } from 'src/app/job/models/job-post/related-jobs.model';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';
import { CacheService } from '../cache.service';
import { EventService } from '../event.service';
import { ChangePayerRequest } from 'src/app/shared/models/maintenance/job.service.model';
import { LocalStorageService } from '../../data/local-storage.service';
import { COUNTRY_ID } from 'src/app/shared/constants/local-storage-items.constants';
import { Job } from '../../../../job/models/job-post/job.model';
import { AssignTradeOptions } from '../../../../job/models/job-post/assign-trade-request.model';
import { RecurringJobsRequest } from 'src/app/job/models/job-detail/details/recurring-jobs-request';
import { RecurringJobsResult } from 'src/app/job/models/job-detail/details/recurring-jobs-response';
import { UpdateDraftedJobRequest } from 'src/app/job/models/job-detail/overview/update-drafted-job-request.model';
import { RelatedJobsRequest } from 'src/app/job/models/job-post/related-jobs-request.model';
import { JobDetailsMode } from 'src/app/job/constants/assign-trade.constant';
import { RelatedPaymentsResult } from 'src/app/job/models/job-detail/details/related-payments-result.model';
import { JobExternalCompleteModel } from 'src/app/job/models/job-detail/complete/job-external-complete-modal';
import { UpdateRecurringJobRequest } from 'src/app/job/models/job-post/update-recurring-job-request.model';
import { RemoveRecurringJobRequest } from 'src/app/job/models/job-detail/remove-reccurance/remove-recurring-job-request.model';
import { CombineJobRequest } from 'src/app/job/models/job-detail/combine/combine-job.request';
import { CombinedJobResult } from 'src/app/job/models/job-detail/combine/combine-job.result';
import { JobSummaryPredictionRequest, TimelineRequest } from 'src/app/job/models/job-detail/activity/job-detail-activity.model';

@Injectable({
  providedIn: 'root',
})
export class JobService extends BaseService {
  private readonly urlPrefix = `${environment.maintenance.urlPrefix}/job`;
  private currentJobId: string;
  private currentVersion: string;
  constructor(
    http: HttpClient,
    private eventService: EventService,
    private cacheService: CacheService,
    private localStorageService: LocalStorageService
  ) {
    super(http);
  }

  jobPost(request: JobPostRequest) {
    return this.post<any>(`${this.urlPrefix}/post`, request).pipe(
      tap(() => this.eventService.dispatch(Events.JobPostedEvent))
    );
  }

  public changePayer(data: ChangePayerRequest) {
    return this.patch<string>(`${this.urlPrefix}/change-payer`, data).pipe(
      tap(() => this.eventService.dispatch(Events.JobPayerChangedEvent))
    );
  }

  public getOverview(
    jobId?: string,
    version?: string
  ): Observable<JobOverviewResult> {
    if (jobId) this.currentJobId = jobId;
    if (version) this.currentVersion = version;
    if (!this.currentJobId && !this.currentVersion) return of();
    return this.cacheService.get(
      `${this.constructor.name}_${this.getOverview.name}`,
      `${this.currentJobId}${this.currentVersion}`,
      this.get<JobOverviewResult>(
        `${this.urlPrefix}/overview?JobId=${this.currentJobId}`
      ),
      [
        Events.JobStatusChangedEvent,
        Events.JobEditedEvent,
        Events.JobAttachmentEditEvent,
        Events.DraftedJobUpdateEvent,
        Events.JobRejectedEvent,
        Events.UpdateRecurringJobEvent,
        Events.InviteTradesToJob,
        Events.JobEmailSentEvent,
        Events.JobNoteAddedEvent,
        Events.JobPayerChangedEvent
      ]
    );
  }

  public getReviewPending() {
    return this.get<string[]>(`${this.urlPrefix}/review-pending`);
  }

  relatedJobs$: Observable<RelatedJobs>;
  getRelatedJobs(request: RelatedJobsRequest) {
    this.relatedJobs$ = this.get<RelatedJobs>(
      `${this.urlPrefix}/related-jobs${this.queryParams(request)}`
    ).pipe(shareReplay(1));
    return this.relatedJobs$;
  }

  getRelatedPayments(jobId: string) {
    return this.get<Array<RelatedPaymentsResult>>(
      `${this.urlPrefix}/related-payments/${jobId}`
    );
  }
  getHasRelatedPayments(jobId: string) {
    return this.get<boolean>(`${this.urlPrefix}/has-related-payments/${jobId}`);
  }

  private sugesstedTags$: Observable<any>;
  private sugesstedTagsParam: string;
  getSugesstedTags(title: string, description: string) {
    var countryId = this.localStorageService.get(COUNTRY_ID);
    const params = this.queryParams({
      Title: title,
      Description: description,
      CountryId: countryId,
    });

    if (this.sugesstedTagsParam != params) {
      this.sugesstedTagsParam = params;
      this.sugesstedTags$ = this.get<any>(
        `${this.urlPrefix}/suggested-tags${params}`
      ).pipe(
        map((res) => {
          res.push('Urgent_fix ', 'Within_a_few_days');
          return res;
        }),
        shareReplay(1)
      );
    }

    return this.sugesstedTags$;
  }

  getAccessContact(propertyId: string) {
    return this.get<string>(`${this.urlPrefix}/access-contact/${propertyId}`);
  }

  getWorkOrderHTML(jobId: string, isQuoteRequest: boolean) {
    return this.get<string>(
      `${this.urlPrefix}/work-order-html/${jobId}/${isQuoteRequest}`
    );
  }

  assignTrades(request: AssignTradeRequest) {
    return this.post<any>(`${this.urlPrefix}/re-award`, request).pipe(
      tap(() => this.eventService.dispatch(Events.ReAwardJobEvent))
    );
  }

  relatedJobUpdate(request: UpdateDraftedJobRequest) {
    return this.put<any>(`${this.urlPrefix}/related-jobs/update`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.DraftedJobUpdateEvent);
      })
    );
  }

  updateRecurringJob(request: UpdateRecurringJobRequest) {
    return this.put<any>(`${this.urlPrefix}/recurring/update`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.UpdateRecurringJobEvent);
      })
    );
  }

  removeRecurringJob(request: RemoveRecurringJobRequest) {
    return this.put<any>(`${this.urlPrefix}/recurring/remove`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.UpdateRecurringJobEvent);
      })
    );
  }
  getWorkOrderPDF(jobId: string, isQuoteRequest: boolean) {
    return this.getBlob(
      `${this.urlPrefix}/work-order-pdf/${jobId}/${isQuoteRequest}`
    );
  }
  getGoogleMapLink(jobId: string) {
    return this.get<any>(
      `${this.urlPrefix}/map-view/${jobId}`
    );
  }

  edit(id: string) {
    return this.get<any>(`${this.urlPrefix}/edit/${id}`).pipe(
      map((res) => {
        const job = Job.getInstance();
       job.mode = JobDetailsMode.EDIT;
        job.title = res.title;
        job.description = res.description;
        job.tenant = res.tenant;
        job.property = res.property;
        job.primaryJobType = res.primaryJobType;
        job.secondaryJobType = res.secondaryJobType;
        job.keyCollectionType = res.keyCollectionType;
        job.budget = res.jobBudget;
        job.payer = res.payer;
        job.issueReportedType = res.issueReportedType;
        job.attachments = res.attachments;
        job.recurrence = res.recurrence;
        job.assignTrades.trades = res.tradees;
        job.defultTimeZoneDiffMinutes = res.defultTimeZoneDiffMinutes;
        job.priority = res.priority;
        job.maintenanceType = res.maintenanceType;
        job.isTenantNotRequired = res.isTenantNotRequired;
        job.isChildRecurringJob = res.isChildRecurringJob;
        job.isStrataMasterEnabled = res.isStrataMasterEnabled;
        job.accessContact = res.accessContact;
        job.notifyTrade = res.notifyTrade;
        job.reasonNotToNotifyTrade = res.reasonNotToNotifyTrade;
        job.specialNoteToTrade = res.specialNoteToTrade;
        job.customKeyCollectionValue = res.customKeyCollectionValue;
        job.workflowStatusId = res.workflowStatusId;
        job.thirdPartyName = res.thirdPartyName;
        job.thirdPartyEmailAddress = res.thirdPartyEmailAddress;
        job.jobWarningNotes = res.jobWarningNotes;
        job.outboundPartnerId = res.outboundPartnerId;
        job.assignTrades.targetTadeeOption = res.targetTardeOption;
        const options = AssignTradeOptions.getInstance();
        options.jobId = job.id;
        options.suburbId = job.property.suburbId;
        options.secondaryJobTypeId = job.secondaryJobType?.id;
        options.targetTardeOption = res.targetTardeOption;
        options.selectedTrades = res.tradees;
        options.primaryJobTypeId = job.primaryJobType?.id;
        job.assets = res.assetsForJob;
        job.ownerThreshold = res.ownerThreshold;
        return job;
      })
    );
  }

  update(request: JobPostRequest) {
      return this.put<any>(`${this.urlPrefix}/update`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEditedEvent);
        if (!request.notifyTrade && request.reasonNotToNotifyTrade)
          this.eventService.dispatch(Events.JobNoteAddedEvent);
      })
    );
  }

  reject(request: JobRejectModel) {
    return this.post<any>(`${this.urlPrefix}/reject`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobRejectedEvent);
      })
    );
  }

  ownerOverrideUpdate(request: any) {
    return this.post<any>(
      `${this.urlPrefix}/add-owner-override-reason`,
      request
    ).pipe(
      tap(() => {
          this.eventService.dispatch(Events.JobOwnerOverridedEvent);
      })
    );
  }

  updateJobDeactivateStatus(jobDeactivateModel: JobRejectModel, anotherJob: boolean = false) {
    return this.put<any>(
      `${this.urlPrefix}/deactivate`,
      jobDeactivateModel
    ).pipe(
      tap(() => {
        if(anotherJob)
        {
          this.eventService.dispatch(Events.RelatedJobStatusChangedEvent);
        }
        else{
          this.eventService.dispatch(Events.JobDeactivateEvent);
        }
      })
    );
  }
  combineJobs(jobs: CombineJobRequest) {
    return this.put<any>(`${this.urlPrefix}/combine`, jobs).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobDeactivateEvent);
      })
    );
  }

  cloneRequest(id: string) {
    return this.get<any>(`${this.urlPrefix}/clone/${id}`).pipe(
      map((res) => {
        const job = Job.getInstance();
        job.title = res.title;
        job.description = res.description;
        job.tenant = res.tenant;
        job.property = res.property;
        job.primaryJobType = res.primaryJobType;
        job.secondaryJobType = res.secondaryJobType;
        job.keyCollectionType = res.keyCollectionType;
        job.budget = res.jobBudget;
        job.payer = res.payer;
        job.defaultPayer = res.payer;
        job.issueReportedType = res.issueReportedType;
        job.attachments = res.attachments;
        job.recurrence = res.recurrence;
        job.owners = res.owners;
        job.assignPms = res.assignPms;
        job.accessContact = res.accessContact;
        job.isStrataMasterEnabled = res.isStrataMasterEnabled;
        job.isTenantNotRequired = res.isTenantNotRequired;
        job.customKeyCollectionValue = res.customKeyCollectionValue;
        job.jobWarningNotes = res.jobWarningNotes;
        job.priority = res.priority;
        job.maintenanceType = res.maintenanceType;
        job.assets = res.assetsForJob;

        const options = AssignTradeOptions.getInstance();
        options.suburbId = job.property.suburbId;
        options.secondaryJobTypeId = job.secondaryJobType?.id;
        return job;
      })
    );
  }

  updateJobPriority(request: any) {
    return this.patch<any>(`${this.urlPrefix}/update/priority`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobEditedEvent);
      })
    );
  }

  getRecurringJobs(recurringJobsRequest: RecurringJobsRequest) {
    return this.get<RecurringJobsResult>(
      `${this.urlPrefix}/recurring${this.queryParams(recurringJobsRequest)}`
    ).pipe(
      tap(() => {
        this.eventService.dispatch(Events.UpdateRecurringJobEvent);
      })
    );
  }

  getIsLastRecurringJob(id: string) {
    return this.get<any>(`${this.urlPrefix}/recurring/last-recurring/${id}`);
  }

  getJobTroubleShootGuide(id: string) {
    return this.get<any>(
      `${this.urlPrefix}/job-troubleshoot-guides?maintenanceTroubleshootGuideId=${id}`
    );
  }

  getPartnerJobType(id: string) {
    return this.get<any>(`${this.urlPrefix}/partner-job-type/${id}`);
  }

  getReview(bidId: string) {
    return this.get<any>(`${this.urlPrefix}/review/${bidId}`);
  }

  jobExternalComplete(jobExternalCompleteModel: JobExternalCompleteModel) {
    return this.put<any>(
      `${this.urlPrefix}/external-complete`,
      jobExternalCompleteModel
    ).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobCompletedEvent);
      })
    );
  }

  moveToPendingInvoice(request: any) {
    return this.patch<any>(`${this.urlPrefix}/pending-invoice`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobMovedToPendingInvoice);
      })
    );
  }

  getCombinedJobs(jobId: string) {
    return this.get<CombinedJobResult[]>(
      `${this.urlPrefix}/combined?JobId=${jobId}`
    );
  }

  getAttachmentData(fileUrl: string) {
    return this.getAttachmentFromBlobURL(fileUrl);
  }

  moveToInProgress(request: any) {
    return this.patch<any>(`${this.urlPrefix}/In-progress`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobStatusChangedEvent);
      })
    );
  }

  convertJob(request: any) {
    return this.post<any>(`${this.urlPrefix}/convert`, request).pipe(
      tap(() => {
        this.eventService.dispatch(Events.JobConvertEvent)
      })
    );
  }

  getJobDescriptionSummary(description: string) {
    return this.get<any>(
      `${this.urlPrefix}/job-description-summary?description=${description}`
    );
  }
  public getJobdescriptiontimelonenotes(JobSummaryPredictionReq: JobSummaryPredictionRequest) {
   const queryParams = this.queryParams(JobSummaryPredictionReq);
    return this.get<any>(`${this.urlPrefix}/job-description-timeline-notes-summary${queryParams}`);
  }
  public checkWaranteeJobAvilability(PrimaryJobTypeId :string,secondaryJobTypeId:string){
    return this.get<any>(
      `${this.urlPrefix}/job-maintainance-type?PrimaryJobTypeId=${PrimaryJobTypeId}&SecondarJobTypeId=${secondaryJobTypeId} `
    );
  }
}
